import { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  ShieldCheck, FileText, Cookie, Lock, Eye, ArrowUpRight, Loader2,
} from 'lucide-react';

/**
 * Legal & Privacy section for both Worker and Manager Profile pages.
 *
 * Pulls the user's full acceptance history from /api/legal/my-acceptances
 * and renders, for each document type:
 *   - Document name + icon
 *   - Current version (and accepted version if different)
 *   - Date the user accepted it
 *   - Link to view the public document
 *
 * Also surfaces always-available information links for Data Retention and
 * UK GDPR Rights — these don't require a recorded acceptance but the user
 * should have a permanent way to read them from inside the app.
 */
function formatAcceptedDate(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch {
    return null;
  }
}

const ACCEPTED_DOCS = [
  {
    key: 'privacy',
    label: 'Privacy Policy',
    description: 'How we collect, use and protect your personal data.',
    icon: ShieldCheck,
    viewHref: '/privacy',
    accent: 'text-[#4338CA] bg-[#4338CA]/10',
  },
  {
    key: 'terms',
    label: 'Terms & Conditions',
    description: 'The rules that govern your use of ShiftCover365.',
    icon: FileText,
    viewHref: '/terms',
    accent: 'text-[#0f1b3d] bg-[#0f1b3d]/10',
  },
  {
    key: 'accountability',
    label: 'Digital Accountability Agreement',
    description: 'Till handling, audit checks and personal responsibility.',
    icon: Lock,
    viewHref: '/terms#accountability',
    accent: 'text-amber-700 bg-amber-100',
  },
];

const INFO_LINKS = [
  {
    key: 'cookies',
    label: 'Cookie Policy',
    description: 'How we use cookies and similar storage.',
    icon: Cookie,
    viewHref: '/cookies',
    accent: 'text-slate-700 bg-slate-100',
  },
  {
    key: 'retention',
    label: 'Data Retention Information',
    description: 'How long we keep your data and why.',
    icon: FileText,
    viewHref: '/privacy#retention',
    accent: 'text-slate-700 bg-slate-100',
  },
  {
    key: 'gdpr',
    label: 'GDPR Rights Information',
    description: 'Your access, correction, deletion and portability rights.',
    icon: Eye,
    viewHref: '/privacy#your-rights',
    accent: 'text-emerald-700 bg-emerald-100',
  },
];

export default function LegalAndPrivacyCard() {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [acceptances, setAcceptances] = useState([]);
  const [currentVersions, setCurrentVersions] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/legal/my-acceptances');
        setAcceptances(res?.data?.acceptances || []);
        setCurrentVersions(res?.data?.currentVersions || {});
      } catch {
        // Non-fatal — the section still renders with links + "Not yet accepted"
      } finally {
        setLoading(false);
      }
    })();
  }, [api]);

  // For each accepted doc type, pick the MOST RECENT acceptance row.
  const latestByType = acceptances.reduce((map, row) => {
    const t = row?.documentType;
    if (!t) return map;
    if (!map[t] || new Date(row.acceptedAt) > new Date(map[t].acceptedAt)) {
      map[t] = row;
    }
    return map;
  }, {});

  return (
    <Card className="border-slate-200" data-testid="legal-privacy-card">
      <CardContent className="p-5 sm:p-6">
        <div className="flex items-center gap-3 mb-1">
          <div className="h-10 w-10 rounded-xl bg-[#4338CA]/10 flex items-center justify-center">
            <ShieldCheck className="h-5 w-5 text-[#4338CA]" />
          </div>
          <div>
            <h2 className="text-base font-bold text-slate-900">Legal & Privacy</h2>
            <p className="text-[12px] text-slate-500 leading-snug">
              Your acceptance history and key policy documents.
            </p>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center gap-2 py-6 text-slate-500">
            <Loader2 className="h-4 w-4 animate-spin" />
            <p className="text-[12.5px]">Loading your acceptance history…</p>
          </div>
        ) : (
          <>
            <div className="mt-5 space-y-2.5" data-testid="legal-accepted-docs">
              {ACCEPTED_DOCS.map((doc) => {
                const accepted = latestByType[doc.key];
                const acceptedDate = accepted ? formatAcceptedDate(accepted.acceptedAt) : null;
                const acceptedVersion = accepted?.version || null;
                const currentVersion = currentVersions[doc.key] || null;
                const outdated = acceptedVersion && currentVersion && acceptedVersion !== currentVersion;
                const Icon = doc.icon;
                return (
                  <div
                    key={doc.key}
                    className="rounded-lg border border-slate-200 p-3.5 hover:border-slate-300 transition-colors"
                    data-testid={`legal-doc-row-${doc.key}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`h-9 w-9 rounded-lg flex items-center justify-center shrink-0 ${doc.accent}`}>
                        <Icon className="h-4 w-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2 flex-wrap">
                          <p className="text-[13px] font-semibold text-slate-900">{doc.label}</p>
                          {acceptedVersion ? (
                            <Badge
                              variant="outline"
                              className={`text-[10.5px] font-semibold uppercase tracking-wider ${
                                outdated
                                  ? 'bg-amber-50 text-amber-800 border-amber-200'
                                  : 'bg-emerald-50 text-emerald-700 border-emerald-200'
                              }`}
                              data-testid={`legal-doc-version-${doc.key}`}
                            >
                              v{acceptedVersion}{outdated ? ` · update v${currentVersion}` : ''}
                            </Badge>
                          ) : (
                            <Badge
                              variant="outline"
                              className="text-[10.5px] font-semibold uppercase tracking-wider bg-slate-50 text-slate-500 border-slate-200"
                              data-testid={`legal-doc-version-${doc.key}`}
                            >
                              Not yet accepted
                            </Badge>
                          )}
                        </div>
                        <p className="text-[12px] text-slate-500 mt-0.5">{doc.description}</p>
                        <div className="flex items-center justify-between gap-3 mt-2 flex-wrap">
                          <p className="text-[11.5px] text-slate-400" data-testid={`legal-doc-accepted-at-${doc.key}`}>
                            {acceptedDate
                              ? <>Accepted <span className="font-medium text-slate-600">{acceptedDate}</span></>
                              : <>Awaiting your acceptance during onboarding.</>}
                          </p>
                          <a
                            href={doc.viewHref}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[12px] font-semibold text-[#4338CA] hover:underline inline-flex items-center gap-1"
                            data-testid={`legal-doc-view-${doc.key}`}
                          >
                            View document <ArrowUpRight className="h-3.5 w-3.5" />
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mt-6">
              <h3 className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-2.5">
                Information & Rights
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5" data-testid="legal-info-links">
                {INFO_LINKS.map((doc) => {
                  const Icon = doc.icon;
                  return (
                    <a
                      key={doc.key}
                      href={doc.viewHref}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="rounded-lg border border-slate-200 p-3 hover:border-slate-300 hover:bg-slate-50 transition-colors flex items-start gap-3"
                      data-testid={`legal-info-${doc.key}`}
                    >
                      <div className={`h-8 w-8 rounded-lg flex items-center justify-center shrink-0 ${doc.accent}`}>
                        <Icon className="h-4 w-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-[12.5px] font-semibold text-slate-900">{doc.label}</p>
                          <ArrowUpRight className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                        </div>
                        <p className="text-[11.5px] text-slate-500 mt-0.5 leading-snug">{doc.description}</p>
                      </div>
                    </a>
                  );
                })}
              </div>
            </div>

            <div className="mt-5">
              <Button
                variant="outline"
                size="sm"
                className="rounded-full text-[12px] h-9 border-slate-300 text-slate-700"
                onClick={() => window.open('mailto:support@shiftcover365.co.uk?subject=Data%20request%20%E2%80%94%20ShiftCover365', '_self')}
                data-testid="legal-request-data-btn"
              >
                Request a copy of my data
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
